import { useEffect, useMemo, useState } from 'react';
import type { Photo, RunDetail, RunSummary } from './types';
import { PhotoLightbox } from './PhotoLightbox';
import { loadRunDetail } from './data';
import { useIsDesktop } from './useMedia';
import {
  formatDate,
  formatDistance,
  formatDuration,
  formatElevation,
  formatPace,
  typeLabel,
  M_PER_MILE,
  M_PER_KM
} from './format';
import { Sparkline } from './Sparkline';
import { computeSplits, computePaceSeries, computeHrZones, type HrZoneBucket } from './analytics';
import { config } from '../config';
import { Carousel, SectionCard, type CarouselItem } from './Carousel';

interface Props {
  run: RunSummary;
  photos?: Photo[];
  onClose: () => void;
  onPlayFlyby?: () => void;
}

export function DetailPanel({ run, photos = [], onClose, onPlayFlyby }: Props) {
  const runPhotos = useMemo(
    () => photos.filter((p) => p.runId === run.id),
    [photos, run.id]
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    setLightboxIndex(null);
  }, [run.id]);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    setDetail(null);
    let cancelled = false;
    setLoading(true);
    loadRunDetail(run.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [run.id]);

  const unit = config.distanceUnit;
  const stepMeters = unit === 'mi' ? M_PER_MILE : M_PER_KM;

  const splits = useMemo(() => (detail ? computeSplits(detail.points, unit) : []), [detail, unit]);
  const paceSeries = useMemo(
    () => (detail ? computePaceSeries(detail.points, unit) : []),
    [detail, unit]
  );
  const hrZones = useMemo(() => (detail ? computeHrZones(detail.points) : null), [detail]);

  const fill = !isDesktop;
  const items: CarouselItem[] = [];

  items.push({
    key: 'overview',
    label: 'Overview',
    render: () => (
      <SectionCard title="Overview" fill={fill}>
        <div className="grid grid-cols-3 gap-3 md:gap-4 w-full">
          <Stat label="Distance" value={formatDistance(run.distance_m, unit)} />
          <Stat label="Time" value={formatDuration(run.duration_s)} />
          <Stat label="Pace" value={formatPace(run.avg_pace_s_per_km, unit)} />
          <Stat label="Elev gain" value={formatElevation(run.elevation_gain_m, 'ft')} />
          <Stat label="Avg HR" value={run.avg_hr ? `${run.avg_hr} bpm` : '—'} />
          <Stat label="Max HR" value={run.max_hr ? `${run.max_hr} bpm` : '—'} />
        </div>
      </SectionCard>
    )
  });

  items.push({
    key: 'pace',
    label: 'Pace',
    render: () => (
      <SectionCard title="Pace" fill={fill}>
        {loading || !detail ? (
          <ChartSkeleton />
        ) : (
          <Sparkline
            x={detail.points.map((p) => p.d / stepMeters)}
            y={paceSeries}
            color="#F25C54"
            fillAlpha={0.12}
            height={140}
            yFormat={(v) => formatPaceShort(v)}
            xFormat={(v) => `${v.toFixed(1)}${unit}`}
            invertY
          />
        )}
      </SectionCard>
    )
  });

  items.push({
    key: 'elevation',
    label: 'Elevation',
    render: () => (
      <SectionCard title="Elevation" fill={fill}>
        {loading || !detail ? (
          <ChartSkeleton />
        ) : (
          <Sparkline
            x={detail.points.map((p) => p.d / stepMeters)}
            y={detail.points.map((p) => p.ele * 3.28084)}
            color="#0B0D10"
            fillAlpha={0.06}
            height={140}
            yFormat={(v) => `${Math.round(v)}ft`}
            xFormat={(v) => `${v.toFixed(1)}${unit}`}
          />
        )}
      </SectionCard>
    )
  });

  if (run.avg_hr !== undefined) {
    items.push({
      key: 'hr',
      label: 'Heart rate',
      render: () => (
        <SectionCard title="Heart rate" fill={fill}>
          {loading || !detail ? (
            <ChartSkeleton />
          ) : (
            <Sparkline
              x={detail.points.map((p) => p.d / stepMeters)}
              y={detail.points.map((p) => (typeof p.hr === 'number' ? p.hr : null))}
              color="#F25C54"
              fillAlpha={0.14}
              height={140}
              yFormat={(v) => `${Math.round(v)}`}
              xFormat={(v) => `${v.toFixed(1)}${unit}`}
            />
          )}
        </SectionCard>
      )
    });
  }

  if (hrZones && hrZones.some((z) => z.seconds > 0)) {
    items.push({
      key: 'zones',
      label: 'HR zones',
      render: () => (
        <SectionCard title="Heart rate zones" fill={fill}>
          <HrZonesContent zones={hrZones} />
        </SectionCard>
      )
    });
  }

  if (splits.length > 0) {
    items.push({
      key: 'splits',
      label: 'Splits',
      render: () => (
        <SectionCard title="Splits" fill={fill} scrollable>
          <SplitsContent splits={splits} unit={unit} stepMeters={stepMeters} />
        </SectionCard>
      )
    });
  }

  if (runPhotos.length > 0) {
    items.push({
      key: 'photos',
      label: 'Photos',
      render: () => (
        <SectionCard title="Photos" fill={fill} scrollable>
          <PhotosGrid photos={runPhotos} onOpen={setLightboxIndex} />
        </SectionCard>
      )
    });
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <header className="flex items-start justify-between gap-3 p-4 pb-3 md:p-6 md:pb-4 border-b border-ink-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`pill ${typePillClasses(run.type)}`}>{typeLabel(run.type)}</span>
            <span className="text-xs text-ink-500 tracking-wide">
              {formatDate(run.date)} · {formatStartTime(run.date)}
            </span>
          </div>
          <h2 className="font-display text-[22px] md:text-[28px] leading-tight text-ink-900 truncate">
            {run.name}
          </h2>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {onPlayFlyby && (
            <button
              type="button"
              onClick={onPlayFlyby}
              className="w-9 h-9 rounded-full bg-accent text-white flex items-center justify-center shadow-sm hover:brightness-110 transition"
              aria-label="Play flyby"
            >
              <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
                <path d="M2.5 1.5 L10 6 L2.5 10.5 Z" />
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/70 hover:bg-white border border-ink-100 text-ink-700 flex items-center justify-center transition"
            aria-label="Close run details"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>

      <Carousel items={items} resetKey={run.id} />
      {lightboxIndex !== null && runPhotos[lightboxIndex] && (
        <PhotoLightbox
          photos={runPhotos}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="stat-label">{label}</div>
      <div className="mt-1 font-mono tracking-tight text-ink-900 leading-tight text-[15px] md:text-xl md:font-display truncate">
        {value}
      </div>
    </div>
  );
}

function ChartSkeleton() {
  return <div className="h-[140px] w-full rounded-lg bg-ink-100/60 animate-pulse" />;
}

function SplitsContent({
  splits,
  unit,
  stepMeters
}: {
  splits: ReturnType<typeof computeSplits>;
  unit: 'mi' | 'km';
  stepMeters: number;
}) {
  const paces = splits.map((s) => s.pace_s_per_unit);
  const fastest = Math.min(...paces);
  const slowest = Math.max(...paces);
  const range = Math.max(1, slowest - fastest);

  return (
    <div className="-m-3">
      <div className="grid grid-cols-[auto_1fr_auto_auto] gap-x-3 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-medium border-b border-ink-100">
        <span>#</span>
        <span>Pace</span>
        <span className="text-right">Time</span>
        <span className="text-right">HR</span>
      </div>
      {splits.map((s) => {
        const partial = s.distance_m < stepMeters * s.index;
        const isFastest = s.pace_s_per_unit === fastest;
        const ratio = 1 - (s.pace_s_per_unit - fastest) / range;
        return (
          <div
            key={s.index}
            className="grid grid-cols-[auto_1fr_auto_auto] gap-x-3 items-center px-4 py-2 text-xs border-b border-ink-100/50 last:border-b-0"
          >
            <span className="font-mono text-ink-500 w-5">
              {partial ? (s.distance_m / stepMeters).toFixed(2) : s.index}
            </span>
            <span className="relative">
              <span
                className="absolute inset-y-1 left-0 rounded-full bg-accent/20"
                style={{ width: `${Math.max(6, ratio * 100)}%` }}
                aria-hidden
              />
              <span
                className={`relative font-mono ${
                  isFastest ? 'text-accent font-medium' : 'text-ink-900'
                }`}
              >
                {formatPaceShort(s.pace_s_per_unit)}
              </span>
            </span>
            <span className="text-right font-mono text-ink-700">{formatDuration(s.duration_s)}</span>
            <span className="text-right font-mono text-ink-500">{s.avg_hr ? s.avg_hr : '—'}</span>
          </div>
        );
      })}
      <div className="px-4 py-2 text-[10px] text-ink-500 tracking-wide">
        Fastest {unit} highlighted · final row may be a partial {unit}
      </div>
    </div>
  );
}

function HrZonesContent({ zones }: { zones: HrZoneBucket[] }) {
  const total = zones.reduce((s, z) => s + z.seconds, 0);
  const colors = ['#C6E4F6', '#8FD3B8', '#F3D27A', '#F2A16A', '#F25C54'];

  return (
    <div className="space-y-2.5">
      <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-ink-100">
        {zones.map((z, i) => {
          const pct = total > 0 ? (z.seconds / total) * 100 : 0;
          return (
            <div
              key={z.zone}
              style={{ width: `${pct}%`, background: colors[i] }}
              aria-label={`${z.label}: ${pct.toFixed(0)}%`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {zones.map((z, i) => {
          const pct = total > 0 ? (z.seconds / total) * 100 : 0;
          return (
            <div key={z.zone} className="text-[10px]">
              <div className="flex items-center gap-1 text-ink-500 tracking-wide">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: colors[i] }}
                  aria-hidden
                />
                Z{z.zone}
              </div>
              <div className="mt-0.5 font-mono text-[11px] text-ink-900">
                {formatDuration(z.seconds)}
              </div>
              <div className="font-mono text-[10px] text-ink-500">{pct.toFixed(0)}%</div>
              <div className="font-mono text-[9px] text-ink-300 mt-0.5">
                {z.lower}-{z.upper}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PhotosGrid({
  photos,
  onOpen
}: {
  photos: Photo[];
  onOpen: (index: number) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {photos.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onOpen(i)}
          className="block aspect-square overflow-hidden rounded-lg bg-ink-100 hover:opacity-90 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={`Open photo ${i + 1} of ${photos.length}`}
        >
          <img
            src={p.thumbUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </button>
      ))}
    </div>
  );
}


function formatStartTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatPaceShort(secondsPerUnit: number): string {
  if (!Number.isFinite(secondsPerUnit) || secondsPerUnit <= 0) return '—';
  const m = Math.floor(secondsPerUnit / 60);
  const s = Math.round(secondsPerUnit % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function typePillClasses(type: string): string {
  switch (type) {
    case 'tempo':
      return 'bg-accent-soft text-accent';
    case 'race':
      return 'bg-ink-900 text-white';
    case 'long':
      return 'bg-ink-100 text-ink-700';
    case 'interval':
      return 'bg-amber-100 text-amber-800';
    case 'recovery':
      return 'bg-emerald-100 text-emerald-800';
    case 'easy':
      return 'bg-sky-100 text-sky-800';
    default:
      return 'bg-ink-100 text-ink-700';
  }
}
